/**
 * User profile service — calls backend /user/profile endpoints
 */

import { logger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '';

/** Auth headers helper */
const getAuthHeaders = (): Record<string, string> => {
  const idToken = localStorage.getItem('idToken');
  const language = localStorage.getItem('theychat-language') || 'en';

  return {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    ...(idToken && { Authorization: `Bearer ${idToken}` }),
  };
};

/** User profile shape returned from GET /user/profile */
export interface UserProfile {
  user_id: string;
  email: string;
  display_name: string;
  given_name: string;
  family_name: string;
  age_range: string;
  avatar_url: string;
  subscription_tier: string;
  subscription_status: string;
  credits: {
    live_avatar: number;
    addon: number;
    total: number;
  };
  daily_usage: {
    text_questions_used: number;
    voice_minutes_used: number;
  };
  total_interactions: number;
  created_at: string;
  updated_at: string;
  last_login_at: string;
  profile_status: string;
  email_verified: boolean;
}

/** Fields accepted by PUT /user/profile */
export interface ProfileUpdatePayload {
  display_name?: string;
  given_name?: string;
  family_name?: string;
  avatar_url?: string;
}

/** Fetch the current user profile from the backend DB */
export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/profile`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error ?? `Failed to fetch profile: ${response.status}`,
      );
    }

    const data = (await response.json()) as { profile: UserProfile };
    logger.info('UserService', 'Profile fetched successfully');
    return data.profile;
  } catch (error) {
    logger.error(
      'UserService',
      'Failed to fetch profile',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }
};

/** Update the user profile in the backend DB */
export const updateUserProfileApi = async (
  updates: ProfileUpdatePayload,
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error ?? `Failed to update profile: ${response.status}`,
      );
    }

    const data = (await response.json()) as { message: string };
    logger.info('UserService', 'Profile updated successfully');
    return data;
  } catch (error) {
    logger.error(
      'UserService',
      'Failed to update profile',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }
};
